import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MoreHorizontal,
  Mail,
  Phone,
  MessageSquare,
  Edit2,
  Copy,
  ExternalLink,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import type { CustomerInfo, StoreItem } from '@/types';
import { getCustomerDisplayName } from '@/lib/utils';

interface CustomerTableProps {
  customers: CustomerInfo[];
  stores: StoreItem[];
  isLoading: boolean;
  onEditCustomer: (customer: CustomerInfo) => void;
}

type SortField = 'name' | 'store' | 'conversations' | 'lastActive';
type SortDirection = 'asc' | 'desc';

export const CustomerTable: React.FC<CustomerTableProps> = ({
  customers,
  stores,
  isLoading,
  onEditCustomer,
}) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStoreName = (storeId?: string) => {
    if (!storeId) return 'Todas as Lojas';
    const s = stores.find((st) => st.id === storeId);
    return s ? (s.name || s.storeName || storeId) : storeId;
  };

  const getStoreProvider = (storeId?: string) => {
    if (!storeId) return 'unknown';
    const s = stores.find((st) => st.id === storeId);
    return s?.provider || s?.platform || 'store';
  };

  // Sort customers
  const sortedCustomers = useMemo(() => {
    const list = [...customers];
    list.sort((a, b) => {
      let comp = 0;
      if (sortField === 'name') {
        const nameA = getCustomerDisplayName(a.customerName || a.name, a.customerEmail || a.email).toLowerCase();
        const nameB = getCustomerDisplayName(b.customerName || b.name, b.customerEmail || b.email).toLowerCase();
        comp = nameA.localeCompare(nameB);
      } else if (sortField === 'store') {
        const storeA = (a.storeId || '').toLowerCase();
        const storeB = (b.storeId || '').toLowerCase();
        comp = storeA.localeCompare(storeB);
      } else if (sortField === 'conversations') {
        const cA = a.totalConversations || 0;
        const cB = b.totalConversations || 0;
        comp = cA - cB;
      } else if (sortField === 'lastActive') {
        const dateA = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : 0;
        const dateB = b.lastActiveAt ? new Date(b.lastActiveAt).getTime() : 0;
        comp = dateA - dateB;
      }
      return sortDirection === 'asc' ? comp : -comp;
    });
    return list;
  }, [customers, sortField, sortDirection]);

  // Pagination slice
  const totalItems = sortedCustomers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const paginatedCustomers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedCustomers.slice(start, start + pageSize);
  }, [sortedCustomers, currentPage, pageSize]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado para a área de transferência`);
  };

  const formatRelativeDate = (dateStr?: string | null) => {
    if (!dateStr) return 'Nunca';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getInitials = (name?: string | null) => {
    if (!name) return 'CU';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1.5 h-3.5 w-3.5 text-muted-foreground/60" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-1.5 h-3.5 w-3.5 text-primary" />
    ) : (
      <ArrowDown className="ml-1.5 h-3.5 w-3.5 text-primary" />
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border/60 bg-card overflow-hidden shadow-sm">
        <Table>
          <TableHeader className="bg-muted/40">
            <TableRow className="hover:bg-transparent">
              <TableHead
                className="cursor-pointer select-none text-xs font-bold text-foreground py-3"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center">
                  <span>Cliente</span>
                  {renderSortIcon('name')}
                </div>
              </TableHead>

              <TableHead className="text-xs font-bold text-foreground py-3">
                Detalhes de Contato
              </TableHead>

              <TableHead
                className="cursor-pointer select-none text-xs font-bold text-foreground py-3"
                onClick={() => handleSort('store')}
              >
                <div className="flex items-center">
                  <span>Loja</span>
                  {renderSortIcon('store')}
                </div>
              </TableHead>

              <TableHead
                className="cursor-pointer select-none text-xs font-bold text-foreground py-3 text-center"
                onClick={() => handleSort('conversations')}
              >
                <div className="flex items-center justify-center">
                  <span>Conversas</span>
                  {renderSortIcon('conversations')}
                </div>
              </TableHead>

              <TableHead
                className="cursor-pointer select-none text-xs font-bold text-foreground py-3"
                onClick={() => handleSort('lastActive')}
              >
                <div className="flex items-center">
                  <span>Última Atividade</span>
                  {renderSortIcon('lastActive')}
                </div>
              </TableHead>

              <TableHead className="text-xs font-bold text-foreground text-right py-3 pr-4">
                Ações
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={6} className="py-4 text-center">
                    <div className="h-4 bg-muted/60 rounded animate-pulse w-3/4 mx-auto" />
                  </TableCell>
                </TableRow>
              ))
            ) : paginatedCustomers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-muted-foreground space-y-2">
                  <Users className="h-10 w-10 mx-auto text-muted-foreground/40" />
                  <p className="font-semibold text-foreground text-sm">Nenhum cliente encontrado</p>
                  <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                    Tente ajustar sua busca ou filtro de loja.
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              paginatedCustomers.map((cust) => {
                const displayName = getCustomerDisplayName(cust.customerName || cust.name, cust.customerEmail || cust.email);
                const displayEmail = cust.customerEmail || cust.email;
                const displayPhone = cust.customerPhone || cust.phone;
                const storeName = getStoreName(cust.storeId);
                const provider = getStoreProvider(cust.storeId);

                return (
                  <TableRow
                    key={cust.id || displayName}
                    className="hover:bg-muted/30 transition-colors"
                  >
                    {/* Customer Avatar & Name */}
                    <TableCell className="py-3 font-medium">
                      <div className="flex items-center gap-2.5">
                        <Avatar className="h-8 w-8 text-xs font-bold border border-border/80">
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {getInitials(displayName)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-xs font-semibold text-foreground">{displayName}</p>
                          <p className="text-[10px] text-muted-foreground font-mono">
                            ID: {cust.id || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </TableCell>

                    {/* Contact Info */}
                    <TableCell className="py-3">
                      <div className="space-y-1 text-xs">
                        {displayEmail ? (
                          <div className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground">
                            <Mail className="h-3 w-3 text-muted-foreground/70" />
                            <span className="truncate max-w-[180px]">{displayEmail}</span>
                          </div>
                        ) : null}
                        {displayPhone ? (
                          <div className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground">
                            <Phone className="h-3 w-3 text-muted-foreground/70" />
                            <span>{displayPhone}</span>
                          </div>
                        ) : null}
                        {!displayEmail && !displayPhone && (
                          <span className="text-[11px] text-muted-foreground italic">Sem informações de contato</span>
                        )}
                      </div>
                    </TableCell>

                    {/* Store Badge */}
                    <TableCell className="py-3">
                      <div className="flex items-center gap-1.5">
                        <Badge
                          variant="outline"
                          className={
                            provider === 'shopify'
                              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-[10px]'
                              : provider === 'nuvemshop'
                              ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 text-[10px]'
                              : 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20 text-[10px]'
                          }
                        >
                          {storeName}
                        </Badge>
                      </div>
                    </TableCell>

                    {/* Total Conversations */}
                    <TableCell className="py-3 text-center">
                      <Badge variant="secondary" className="text-xs px-2 py-0.5 font-semibold">
                        <MessageSquare className="h-3 w-3 mr-1 text-primary" />
                        {cust.totalConversations || 0}
                      </Badge>
                    </TableCell>

                    {/* Last Active */}
                    <TableCell className="py-3 text-xs text-muted-foreground">
                      {formatRelativeDate(cust.lastActiveAt)}
                    </TableCell>

                    {/* Actions Menu */}
                    <TableCell className="py-3 text-right pr-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44 text-xs">
                          <DropdownMenuItem
                            onClick={() => onEditCustomer(cust)}
                            className="gap-2 cursor-pointer text-xs"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                            <span>Editar Perfil</span>
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={() => {
                              navigate(`/conversations?customerId=${encodeURIComponent(cust.id || '')}&customerName=${encodeURIComponent(displayName)}`);
                            }}
                            className="gap-2 cursor-pointer text-xs"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            <span>Ver Chats</span>
                          </DropdownMenuItem>

                          <DropdownMenuSeparator />

                          {displayEmail && (
                            <DropdownMenuItem
                              onClick={() => copyToClipboard(displayEmail, 'Email')}
                              className="gap-2 cursor-pointer text-xs"
                            >
                              <Copy className="h-3.5 w-3.5" />
                              <span>Copiar Email</span>
                            </DropdownMenuItem>
                          )}

                          {displayPhone && (
                            <DropdownMenuItem
                              onClick={() => copyToClipboard(displayPhone, 'Telefone')}
                              className="gap-2 cursor-pointer text-xs"
                            >
                              <Copy className="h-3.5 w-3.5" />
                              <span>Copiar Telefone</span>
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination & Summary Footer */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground px-1">
        <div className="flex items-center gap-2">
          <span>Mostrando</span>
          <Select
            value={String(pageSize)}
            onValueChange={(val) => {
              setPageSize(Number(val));
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="h-8 w-16 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5" className="text-xs">5</SelectItem>
              <SelectItem value="10" className="text-xs">10</SelectItem>
              <SelectItem value="20" className="text-xs">20</SelectItem>
              <SelectItem value="50" className="text-xs">50</SelectItem>
            </SelectContent>
          </Select>
          <span>de {totalItems} clientes</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs">
            Página {currentPage} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            className="h-8 px-2.5 text-xs"
          >
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            className="h-8 px-2.5 text-xs"
          >
            Próximo
          </Button>
        </div>
      </div>
    </div>
  );
};
